import { datadogLogs } from '@datadog/browser-logs';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useApiContext } from '../ApiContext';
import { BandwidthContext } from './BandwidthContext';
import { BandwidthStatusEnum } from './types';

const UNSTABLE_DELAY = 1500;
const OFFLINE_DELAY = 5000;
const PING_INTERVAL = 5000;
// Hysteresis: a single failed ping is most often a transient wifi drop (roaming between
// access points). Only flip to offline after consecutive failures to avoid status flapping.
const CONSECUTIVE_FAILURES_BEFORE_OFFLINE = 2;

export const BandwidthProvider: FC<PropsWithChildren> = ({ children }: PropsWithChildren) => {
  const [status, setStatus] = useState<BandwidthStatusEnum>(BandwidthStatusEnum.Online);
  const {
    apiConfig: { url },
  } = useApiContext();

  const statusRef = useRef(status);
  statusRef.current = status;
  const failuresRef = useRef(0);
  const offlineSinceRef = useRef<number | null>(null);

  const goOffline = useCallback((reason: string, context: Record<string, unknown> = {}) => {
    failuresRef.current += 1;

    if (statusRef.current === BandwidthStatusEnum.Offline) {
      return;
    }

    if (failuresRef.current < CONSECUTIVE_FAILURES_BEFORE_OFFLINE) {
      datadogLogs.logger.debug('Health Check failed', { reason, failures: failuresRef.current, ...context });
      return;
    }

    offlineSinceRef.current = Date.now();
    datadogLogs.logger.warn('NETWORK_TRANSITION: Going offline', {
      from: statusRef.current,
      to: BandwidthStatusEnum.Offline,
      reason,
      failures: failuresRef.current,
      ...context,
    });
    setStatus(BandwidthStatusEnum.Offline);
  }, []);

  const ping = useCallback(async () => {
    if (!navigator.onLine) {
      goOffline('navigator_offline');
      return;
    }

    try {
      const start = Date.now();
      const response = await fetch(`${url}/health`, { signal: AbortSignal.timeout(OFFLINE_DELAY) });
      const time = Date.now() - start;

      if (!response.ok) {
        goOffline('health_check_failed', { duration: time, responseStatus: response.status });
        return;
      }

      flushSync(() => {
        failuresRef.current = 0;
        const previousStatus = statusRef.current;

        if (time > UNSTABLE_DELAY) {
          if (previousStatus !== BandwidthStatusEnum.Unstable) {
            datadogLogs.logger.debug('NETWORK_TRANSITION: Connection unstable', {
              from: previousStatus,
              to: BandwidthStatusEnum.Unstable,
              duration: time,
            });
          }
          setStatus(BandwidthStatusEnum.Unstable);
        } else {
          if (previousStatus === BandwidthStatusEnum.Offline) {
            datadogLogs.logger.info('NETWORK_TRANSITION: Back online', {
              from: previousStatus,
              to: BandwidthStatusEnum.Online,
              duration: time,
              offlineDurationMs: offlineSinceRef.current ? Date.now() - offlineSinceRef.current : undefined,
            });
            offlineSinceRef.current = null;
          }
          setStatus(BandwidthStatusEnum.Online);
        }
      });
    } catch {
      goOffline('fetch_failed');
    }
  }, [url, goOffline]);

  useEffect(() => {
    ping();
    const intervalId = setInterval(ping, PING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [ping]);

  return <BandwidthContext.Provider value={{ status, setStatus }}>{children}</BandwidthContext.Provider>;
};
